import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { assertNotNull } from '@core/prelude';
import { ContactSummary } from '@features/contacts/models';
import { Company } from '@features/contacts/models/company';
import { CompanyService } from '@features/contacts/services/company.service';
import { ContactService } from '@features/contacts/services/contact.service';
import { ModalSetting } from '@shared/components/modal/modal-setting';

import { TagEntityType } from '@features/opportunities/services/tag.service';
import { CompanyEditComponent } from '../details/edit/company-edit.component';

export type LoadContent = {
  company: Company.Model;
  contacts: ContactSummary[];
};

@Component({
  selector: 'spark-contacts-company-summary',
  templateUrl: './company-summary.component.html',
  styleUrls: ['./company-summary.component.scss']
})
export class CompanySummaryComponent {
  TagEntityType = TagEntityType;
  @Input() refresh$: BehaviorSubject<null> = new BehaviorSubject<null>(null);

  companyId!: string;
  contacts: ContactSummary[] = [];

  private companyId$: Observable<string> = this.route.paramMap.pipe(
    map((params) => params.get('companyId')),
    assertNotNull(
      'Internal error: CompanySummaryComponent expects companyId parameter in route'
    ),
    tap((id) => (this.companyId = id)),
    shareReplay(1)
  );

  load$: Observable<LoadContent> = combineLatest([
    this.refresh$,
    this.companyId$
  ]).pipe(
    switchMap(([_, companyId]) => {
      return this.companyService.getCompanyForDisplay(companyId).pipe(
        switchMap((company) => {
          let contactIds: string[] = [];
          company.directors.forEach((item) => contactIds.push(item));
          company.shareholders.forEach((item) => contactIds.push(item.id));

          return forkJoin({
            company: of(company),
            contacts: this.contactService
              .getSpecificContacts(contactIds)
              .pipe(tap((x) => (this.contacts = x)))
          });
        })
      );
    })
  );

  private modal: BsModalRef | null = null;

  constructor(
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private contactService: ContactService,
    private companyService: CompanyService
  ) {}

  startDateLabel = $localize`:Start Date Label|Country specific business start date title@@start-date:Date started`;

  public edit(): void {
    this.openFormModal(CompanyEditComponent, this.companyId);
  }

  public contactName(id: string) {
    let contact = this.contacts.find((x) => x.id == id);
    return contact ? contact.name : '';
  }

  private closeModal(): void {
    this.modal?.hide();
  }

  private openFormModal(component: any, companyId: string | null = null) {
    this.closeModal();
    let content: any = {
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    };

    if (companyId) {
      content.companyId = companyId;
    }

    this.modal = this.modalService.show(
      component,
      ModalSetting.createLargeModal(content)
    );
  }

  private closeAndRefresh(): void {
    this.closeModal();
    this.refresh$.next(null);
  }
}
